import type { ReactNode, Ref } from 'react'
import { Icon } from '../../components/Icon'
import { StudioBrand } from './StudioBrand'
import { StudioTagPicker } from './StudioTagPicker'
import type { SearchFilters } from '../../search/noteSearch'

export type StudioDestination = 'all' | 'quick' | 'reminders' | 'links' | 'archive' | 'trash'
export interface DesktopSidebarProps {
  filters: SearchFilters
  editing: boolean
  settingsOpen: boolean
  /** The Links screen is open; `linksEnabled` shows its entry (a build that has it). */
  linksOpen?: boolean
  linksEnabled?: boolean
  busy: boolean
  name: string
  status?: ReactNode
  saveStatus?: string
  progressLabel?: string
  activity?: ReactNode
  tags: readonly string[]
  settingsRef: Ref<HTMLButtonElement>
  onNavigate: (destination: StudioDestination) => void
  onTag: (tag: string) => void
  onSettings: () => void
  onRefresh: () => void
  onNewNote: () => void
  variant?: 'desktop' | 'mobile'
  onClose?: () => void
}
const destinations = [
  ['all', 'layout-grid', 'Everything'], ['quick', 'flash', 'Quick Access'],
  ['reminders', 'bell', 'Reminders'], ['links', 'link', 'Links'], ['archive', 'archive', 'Archive'], ['trash', 'trash', 'Trash'],
] as const

export function DesktopSidebar(props: DesktopSidebarProps) {
  const { filters } = props
  // Links is a screen of its own, listed with the library's views only in a build that has it.
  const linksShown = Boolean(props.linksEnabled)
  const primary = destinations.filter(([destination]) => destination === 'links' ? linksShown : ['all', 'quick', 'reminders'].includes(destination))
  const utilities = destinations.filter(([destination]) => destination === 'archive' || destination === 'trash')
  const selected = (destination: StudioDestination) => !props.editing && !props.settingsOpen && (
    destination === 'links' ? Boolean(props.linksOpen) : props.linksOpen ? false : destination === 'quick' ? !!filters.quickAccess : destination === 'reminders' ? !!filters.hasAlarm
      : destination === 'archive' ? filters.status === 'archived' : destination === 'trash' ? filters.status === 'trash'
        : !filters.quickAccess && !filters.hasAlarm && (filters.status ?? 'all') === 'all'
  )
  return <aside className={`studio-sidebar${props.variant === 'mobile' ? ' studio-sidebar--mobile' : ''}`} aria-label={props.variant === 'mobile' ? 'Workspace navigation' : 'Desktop workspace'}>
    <div className="studio-sidebar__brand-row">
      <StudioBrand onRefresh={props.onRefresh} progressLabel={props.progressLabel}/>
      {props.variant === 'mobile' ? <button className="btn btn-ghost btn-square mobile-sidebar__close" type="button" aria-label="Close navigation" onClick={props.onClose}><span aria-hidden="true">×</span></button> : null}
    </div>
    <button className="btn btn-primary studio-new-note" type="button" onClick={props.onNewNote} disabled={props.busy} aria-label="New note"><Icon name="new"/><span>New note</span></button>
    <nav aria-label="Library"><ul className="menu studio-navigation">{primary.map(([destination, icon, label]) => <li key={destination}><button className={selected(destination) ? 'menu-active' : ''} aria-label={label} aria-pressed={selected(destination)} disabled={props.busy} onClick={() => props.onNavigate(destination)}><Icon name={icon}/><span>{label}</span></button></li>)}</ul></nav>
    <div className="studio-sidebar__collections">
    {/* Tags are a quiet, collapsed section below the library. */}
    <div className="studio-sidebar__tags">
      <StudioTagPicker id="studio-sidebar-tags" collapsible tags={props.tags} selectedTags={[...(filters.tags ?? []), ...(filters.tag ? [filters.tag] : [])]} disabled={props.busy} onSelect={props.onTag}/>
    </div>
    </div>
    <div className="studio-sidebar__bottom"><nav aria-label="Library utilities"><ul className="menu studio-navigation">{utilities.map(([destination, icon, label]) => <li key={destination}><button className={selected(destination) ? 'menu-active' : ''} aria-label={label} aria-pressed={selected(destination)} disabled={props.busy} onClick={() => props.onNavigate(destination)}><Icon name={icon}/><span>{label}</span></button></li>)}</ul></nav><button ref={props.settingsRef} className="btn btn-ghost studio-settings" aria-label="Settings" aria-expanded={props.settingsOpen} onClick={props.onSettings} disabled={props.busy}><Icon name="settings"/>Settings</button><div className="studio-account"><span className="studio-account__avatar">{props.name.charAt(0)}</span><span>{props.name}</span></div><div className="studio-sidebar__status">
      {props.saveStatus ? <span className="studio-save-state" role="status" title={props.saveStatus}><Icon name="check"/><span>{props.saveStatus}</span></span> : null}
      {props.status ? <span className="studio-sync-state" role="status"><span>{props.status}</span></span> : null}
      {props.activity}
    </div></div>
  </aside>
}
